import { gql, useQuery } from '@apollo/client';
import _ from 'lodash';
import { useMemo } from 'react';
import type {
  AllGroupsQuery,
  AllGroupsQueryVariables,
} from '../graphql/generated/graphqlTypes';
import { useAllUsersMap } from '../user/useAllUsers';
import populateObjects from '../utils/populateObjects';

const ALL_GROUPS_QUERY = gql`
  query allGroups {
    session: currentSession {
      organization {
        id
        groups {
          id
          name
          allUsers {
            id
          }
        }
      }
    }
  }
`;

const populateGroupUsers = <
  User,
  Group extends { allUsers?: { id: string }[] },
>(
  groups: Group[],
  userMap: { [id: string]: User },
) =>
  groups.map((group) => ({
    ...group,
    allUsers: group.allUsers && populateObjects(userMap, group.allUsers),
  }));

const useAllGroups = (options = {}) => {
  const userMap = useAllUsersMap(options);
  const response = useQuery<AllGroupsQuery, AllGroupsQueryVariables>(
    ALL_GROUPS_QUERY,
    options,
  );

  return useMemo(
    () => ({
      ...response,
      data: populateGroupUsers(
        response?.data?.session.organization.groups ?? [],
        userMap,
      ),
    }),
    [response, userMap],
  );
};

export const useAllGroupMap = (...args: Parameters<typeof useAllGroups>) => {
  const allGroups = useAllGroups(...args);
  const allGroupMap = useMemo(() => _.keyBy(allGroups.data, 'id'), [allGroups]);

  return allGroupMap;
};

export default useAllGroups;
